/**
 * Airflow API integration functions
 */

import axios, { AxiosError } from 'axios';
import {
  TriggerRequest,
  TriggerResponse,
  PipelineStatus,
  DAGRunsList,
  BatchTriggerRequest,
  BatchTriggerResponse,
  HealthStatus,
  TriggerAndWaitResponse,
  AirflowApiError,
  PipelineMode,
  PipelineState,
  CompletePipelineStatus
} from '../types/airflow';

// API configuration - reuse the same logic from api.ts
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://insights-api-558553787161.us-central1.run.app';


// Error handling utility
const handleAirflowApiError = (error: AxiosError): never => {
  const responseData = error.response?.data as any;
  const airflowError: AirflowApiError = {
    status: error.response?.status || 500,
    message: error.message,
    detail: responseData?.detail || error.response?.statusText || 'Unknown error',
    code: error.code
  };
  
  console.error('Airflow API Error:', airflowError);
  throw airflowError;
};

/**
 * Check Airflow API health and connectivity
 */
export const checkAirflowHealth = async (): Promise<HealthStatus> => {
  try {
    const response = await axios.get<HealthStatus>(`${API_BASE_URL}/airflow/health`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * Trigger a pipeline run for a specific date
 * @param request - Pipeline trigger request parameters
 */
export const triggerPipeline = async (request: TriggerRequest): Promise<TriggerResponse> => {
  try {
    const response = await axios.post<TriggerResponse>(
      `${API_BASE_URL}/airflow/trigger/pipeline`,
      request,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * Get the status of a specific DAG run
 * @param dagId - DAG identifier
 * @param runId - DAG run identifier
 */
export const getPipelineStatus = async (dagId: string, runId: string): Promise<PipelineStatus> => {
  try {
    const response = await axios.get<PipelineStatus>(
      `${API_BASE_URL}/airflow/status/${dagId}/${runId}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * List recent DAG runs for a specific DAG
 * @param dagId - DAG identifier
 * @param limit - Maximum number of runs to return
 * @param state - Filter by state (optional)
 */
export const listPipelineRuns = async (
  dagId: string = 'daily_productivity_pipeline', 
  limit: number = 10,
  state?: PipelineState
): Promise<DAGRunsList> => {
  try {
    const params = new URLSearchParams({
      limit: limit.toString()
    });
    
    if (state) {
      params.append('state', state);
    }
    
    const response = await axios.get<DAGRunsList>(
      `${API_BASE_URL}/airflow/runs/${dagId}?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * Trigger pipeline and wait for completion
 * @param request - Pipeline trigger request parameters
 * @param timeout - Maximum wait time in seconds
 * @param pollInterval - Polling interval in seconds
 */
export const triggerAndWait = async (
  request: TriggerRequest,
  timeout: number = 3600,
  pollInterval: number = 30
): Promise<TriggerAndWaitResponse> => {
  try {
    const params = new URLSearchParams({
      timeout: timeout.toString(),
      poll_interval: pollInterval.toString()
    });
    
    const response = await axios.post<TriggerAndWaitResponse>(
      `${API_BASE_URL}/airflow/trigger/wait?${params.toString()}`,
      request,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * Trigger pipeline for multiple dates (batch operation)
 * @param request - Batch trigger request parameters
 */
export const triggerBatch = async (request: BatchTriggerRequest): Promise<BatchTriggerResponse> => {
  try {
    const response = await axios.post<BatchTriggerResponse>(
      `${API_BASE_URL}/airflow/trigger/batch`,
      request,
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};

/**
 * Convenience function to trigger pipeline for the current date selection
 * @param date - Date from the UI dropdown (YYYY-MM-DD format)
 * @param mode - Pipeline execution mode
 * @param filterUsers - Whether to process filtered users only
 * @param userId - Optional specific user ID to process (single user mode)
 */
export const triggerPipelineForDate = async (
  date: string,
  mode: PipelineMode = PipelineMode.FULL,
  filterUsers: boolean = true,
  userId?: string
): Promise<TriggerResponse> => {
  const supportedModes = new Set<PipelineMode>([PipelineMode.FULL, PipelineMode.ETL_ONLY]);
  const normalizedMode = supportedModes.has(mode) ? mode : PipelineMode.FULL;

  if (!supportedModes.has(mode)) {
    console.warn('Unsupported pipeline mode requested. Falling back to FULL.', { requestedMode: mode });
  }

  const request: TriggerRequest = {
    date,
    mode: normalizedMode,
    filter_users: filterUsers
  };
  
  // Add user_id if provided (single user processing)
  if (userId) {
    request.user_id = userId;
  }
  
  return triggerPipeline(request);
};


/**
 * Check if a pipeline is currently running for a specific date
 * @param date - Date to check (YYYY-MM-DD format)
 * @param dagId - DAG identifier
 */
export const isPipelineRunningForDate = async (
  date: string,
  dagId: string = 'daily_productivity_pipeline'
): Promise<boolean> => {
  try {
    const runningPipeline = await getRunningPipelineForDate(date, dagId);
    return runningPipeline !== null;
  } catch (error) {
    console.error('Error checking if pipeline is running:', error);
    return false;
  }
};

/**
 * Get the most recent pipeline run for a specific date (any state)
 * @param date - Date string (YYYY-MM-DD)
 * @param dagId - DAG ID to check
 * @returns Most recent pipeline details or null if no pipeline found
 */
export const getMostRecentPipelineForDate = async (
  date: string,
  dagId: string = 'daily_productivity_pipeline'
): Promise<{ runId: string; state: PipelineState } | null> => {
  try {
    const runs = await listPipelineRuns(dagId, 50); // Get more runs to find the date
    
    // Find the most recent run for the specified date (any state)
    const runsForDate = runs.dag_runs.filter(run => {
      const runDate = run.logical_date.split('T')[0]; // Extract date part
      return runDate === date;
    });
    
    if (runsForDate.length > 0) {
      // Sort by start_date to get most recent
      const sortedRuns = runsForDate.sort((a, b) => {
        const dateA = new Date(a.start_date || a.logical_date);
        const dateB = new Date(b.start_date || b.logical_date);
        return dateB.getTime() - dateA.getTime(); // Most recent first
      });
      
      return {
        runId: sortedRuns[0].dag_run_id,
        state: sortedRuns[0].state as PipelineState
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error getting most recent pipeline:', error);
    return null;
  }
};

/**
 * Get the running pipeline details for a specific date
 * @param date - Date string (YYYY-MM-DD)
 * @param dagId - DAG ID to check
 * @returns Running pipeline details or null if no pipeline is running
 */
export const getRunningPipelineForDate = async (
  date: string,
  dagId: string = 'daily_productivity_pipeline'
): Promise<{ runId: string; state: PipelineState } | null> => {
  try {
    const runs = await listPipelineRuns(dagId, 10);
    
    // Look for running states for the specified date
    const runningStates = [
      PipelineState.QUEUED,
      PipelineState.RUNNING,
      PipelineState.UP_FOR_RETRY,
      PipelineState.RESTARTING
    ];
    
    const runningRun = runs.dag_runs.find(run => {
      const runDate = run.logical_date.split('T')[0]; // Extract date part
      return runDate === date && runningStates.includes(run.state as PipelineState);
    });
    
    if (runningRun) {
      return {
        runId: runningRun.dag_run_id,
        state: runningRun.state as PipelineState
      };
    }
    
    return null;
  } catch (error) {
    console.error('Error checking if pipeline is running:', error);
    return null; // Return null on error
  }
};

/**
 * Format duration in human-readable format
 * @param seconds - Duration in seconds
 */
export const formatDuration = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`;
  } else if (seconds < 3600) {
    return `${Math.round(seconds / 60)}m`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
};

/**
 * Get user-friendly error message from AirflowApiError
 * @param error - Airflow API error
 */
export const getErrorMessage = (error: AirflowApiError): string => {
  switch (error.status) {
    case 401:
      return 'Authentication failed. Please check your API credentials.';
    case 403:
      return 'Access denied. Insufficient permissions.';
    case 404:
      return 'Pipeline or resource not found.';
    case 409:
      return 'Pipeline is already running for this date.';
    case 503:
      if (error.detail?.includes('not configured')) {
        return 'Airflow integration is not configured. Please contact your administrator.';
      }
      return 'Airflow service is currently unavailable.';
    case 500:
      return 'Internal server error. Please try again later.';
    default:
      return error.detail || error.message || 'An unexpected error occurred.';
  }
};

/**
 * Get complete pipeline status for both ETL and Insights DAGs
 * @param date - Date to check (YYYY-MM-DD format)
 */
export const getCompletePipelineStatus = async (date: string): Promise<CompletePipelineStatus> => {
  try {
    const response = await axios.get<CompletePipelineStatus>(
      `${API_BASE_URL}/airflow/status/complete/${date}`
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      return handleAirflowApiError(error);
    }
    throw error;
  }
};


/**
 * Check if complete pipeline is running for a specific date
 * @param date - Date to check (YYYY-MM-DD format)
 */
export const isCompletePipelineRunning = async (date: string): Promise<boolean> => {
  try {
    const status = await getCompletePipelineStatus(date);
    const runningStates = ['queued', 'running', 'waiting'];
    return runningStates.includes(status.overall_state);
  } catch (error) {
    console.error('Error checking if complete pipeline is running:', error);
    return false;
  }
};
